// Router rate limiting (fsgnext#191) — THE one implementation
// (ui-conventions.md #68).
//
// The Cosmo router answers an over-limit operation with HTTP 429 and
// `{"errors":[{"message":"Rate limit exceeded","extensions":{"code":
// "RATE_LIMIT_EXCEEDED"}}],"data":null,"extensions":{"rateLimit":{
// "retryAfterMs":…}}}` — no Retry-After header. One denial starts ONE
// app-wide cooldown and retry with bounded backoff; mutations are never
// retried and are held back while the cooldown is active.
//
// The limit counts SUBGRAPH fetches, so a mutation can be denied part-way
// after one subgraph already committed. Its message therefore says the change
// may already have been saved, and nothing here ever re-sends a mutation; a
// re-submit carries the same idempotency key (src/core/intentKeys.ts).
import * as Sentry from '@sentry/vue'
import {
  CombinedError,
  makeOperation,
  type Exchange,
  type Operation,
  type OperationResult,
} from '@urql/vue'
import { getOperationAST, parse } from 'graphql'
import { Notify } from 'quasar'
import { filter, map, merge, onPush, pipe, share } from 'wonka'

export const RATE_LIMIT_CODE = 'RATE_LIMIT_EXCEEDED'

const MIN_RETRY_MS = 5_000
const MAX_RETRY_MS = 60_000
const MAX_QUERY_RETRIES = 4
const BACKOFF_BASE_MS = 1_000
const POLL_BACKOFF_MAX_MS = 15 * 60_000

/** End of the current cooldown (epoch ms); 0 when none has started. */
let cooldownUntil = 0
/** Whether the current cooldown window has shown its one notice. */
let noticeShown = false

/** Test seam: forget any cooldown. */
export function resetRateLimit(): void {
  cooldownUntil = 0
  noticeShown = false
}

export function cooldownRemainingMs(now = Date.now()): number {
  return Math.max(0, cooldownUntil - now)
}

type ErrorShape = {
  extensions?: Record<string, unknown> | null
  graphQLErrors?: { extensions?: Record<string, unknown> | null }[]
  response?: { status?: unknown } | null
}

const deniedCode = (e: { extensions?: Record<string, unknown> | null } | undefined) =>
  e?.extensions?.code === RATE_LIMIT_CODE

/** A rate-limited result: a CombinedError whose response was a 429 (also when
 *  a proxy's non-JSON 429 made it a network error), one carrying the router's
 *  code, or a single GraphQL error with that code. */
export function isRateLimited(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false
  const e = error as ErrorShape
  return e.response?.status === 429 || deniedCode(e) || (e.graphQLErrors ?? []).some(deniedCode)
}

/** Prefer server retry guidance; otherwise use capped exponential backoff with jitter. */
export function retryDelayMs(
  error: unknown,
  attempt: number,
  random = Math.random(),
  now = Date.now(),
): number {
  const e = error as {
    response?: { headers?: { get?: (name: string) => string | null } } | null
    extensions?: { rateLimit?: { retryAfterMs?: unknown } } | null
  } | null
  // Retry-After is clamped like the body hint; 0 or an unreadable value
  // waits the minimum.
  const header = e?.response?.headers?.get?.('Retry-After')
  if (header != null) {
    const seconds = Number(header)
    const date = Number.isFinite(seconds) ? now + seconds * 1_000 : Date.parse(header)
    const ms = Number.isFinite(date) ? date - now : MIN_RETRY_MS
    return Math.min(MAX_RETRY_MS, Math.max(MIN_RETRY_MS, ms))
  }
  const hint = e?.extensions?.rateLimit?.retryAfterMs
  if (typeof hint === 'number' && Number.isFinite(hint)) {
    return Math.min(MAX_RETRY_MS, Math.max(MIN_RETRY_MS, hint))
  }
  const exponential = Math.min(MAX_RETRY_MS, BACKOFF_BASE_MS * 2 ** Math.max(0, attempt - 1))
  return Math.min(MAX_RETRY_MS, Math.round(exponential * (0.5 + Math.min(1, Math.max(0, random)))))
}

export function rateLimitPollDelayMs(failures: number, random = Math.random()): number {
  const exponential = Math.min(POLL_BACKOFF_MAX_MS, 60_000 * 2 ** Math.max(0, failures))
  return Math.min(
    POLL_BACKOFF_MAX_MS,
    Math.round(exponential * (0.5 + Math.min(1, Math.max(0, random)))),
  )
}

function queryRequest(init?: RequestInit): boolean {
  const body = init?.body
  if (typeof body !== 'string') return false
  try {
    const request = JSON.parse(body) as { query?: unknown; operationName?: string }
    if (typeof request.query !== 'string') return false
    return getOperationAST(parse(request.query), request.operationName)?.operation === 'query'
  } catch {
    return false
  }
}

function wait(ms: number, signal?: AbortSignal | null): Promise<void> {
  if (ms <= 0 || signal?.aborted) return Promise.resolve()
  return new Promise((resolve) => {
    const timer = setTimeout(done, ms)
    function done() {
      clearTimeout(timer)
      signal?.removeEventListener('abort', done)
      resolve()
    }
    signal?.addEventListener('abort', done, { once: true })
  })
}

/** Retry query operations only; a mutation may have committed before a 429. */
export function rateLimitFetch(fetcher: typeof fetch): typeof fetch {
  return async (input, init) => {
    const graphqlQuery = queryRequest(init)
    const method = init?.method ?? (input instanceof Request ? input.method : 'GET')
    if (!graphqlQuery && method.toUpperCase() !== 'GET') return fetcher(input, init)
    const signal = init?.signal
    let attempt = 0
    while (true) {
      const remaining = cooldownRemainingMs()
      if (remaining > 0) await wait(remaining, signal)
      const response = await fetcher(input, init)
      // The router denies with HTTP 429, so any other answer goes through
      // unread: parsing every body twice would cost the large ones.
      if (response.status !== 429 || attempt >= MAX_QUERY_RETRIES) return response
      let payload: unknown
      try {
        payload = await response.clone().json()
      } catch {
        payload = undefined
      }
      attempt++
      const delay = retryDelayMs(
        { response, extensions: (payload as { extensions?: unknown })?.extensions },
        attempt,
      )
      startCooldown(delay, 'query')
      if (!noticeShown) {
        noticeShown = true
        Notify.create({
          type: 'warning',
          message: `Too many requests, retrying in ${Math.ceil(delay / 1_000)}s`,
        })
      }
      await wait(delay, signal)
    }
  }
}

/** True when the error is one rateLimitExchange made up: the operation never
 *  left the browser. */
function notSent(error: unknown): boolean {
  const e = error as ErrorShape | null | undefined
  return (e?.graphQLErrors ?? []).some((g) => deniedCode(g) && g.extensions?.notSent === true)
}

/** The friendly text for a rate-limited error, in place of the router's
 *  "Rate limit exceeded". `mutation` adds what the user must know before
 *  pressing save again. */
export function rateLimitMessage(error: unknown, kind: 'query' | 'mutation' = 'query'): string {
  const s = Math.ceil(cooldownRemainingMs() / 1000)
  const wait = `${s} second${s === 1 ? '' : 's'}`
  if (kind === 'mutation' && !notSent(error)) {
    return `Too many requests — the change may already have been saved. Check before repeating it${s > 0 ? `, and wait ${wait}` : ''}.`
  }
  if (kind === 'mutation') return 'Too many requests — the change was not sent. Please try again.'
  return 'Too many requests. Please try again.'
}

function startCooldown(ms: number, operation: Operation['kind']): void {
  const now = Date.now()
  if (now >= cooldownUntil) {
    noticeShown = false
    // One breadcrumb per window, not an event per operation: a denial is the
    // limiter doing its job, and tracing.ts's isFault already skips its code.
    Sentry.addBreadcrumb({
      category: 'graphql',
      level: 'warning',
      message: 'rate limited by the router',
      data: { operation, retryAfterMs: ms },
    })
  }
  cooldownUntil = Math.max(cooldownUntil, now + ms)
}

function heldResult(operation: Operation): OperationResult {
  return {
    operation,
    data: undefined,
    error: new CombinedError({
      graphQLErrors: [
        { message: 'Rate limit exceeded', extensions: { code: RATE_LIMIT_CODE, notSent: true } },
      ],
    }),
    stale: false,
    hasNext: false,
  }
}

function observe(result: OperationResult): void {
  if (!isRateLimited(result.error)) return
  if (!notSent(result.error))
    startCooldown(
      retryDelayMs({ response: result.error?.response, extensions: result.extensions }, 1),
      result.operation.kind,
    )
  // One notice per window, whatever was denied first: many pages (the public
  // site among them, the one tier enforced at cutover) render a fixed "could
  // not be loaded" instead of the error, so this is the only place the wait
  // shows. Polls and everything held back after it stay silent.
  if (noticeShown) return
  noticeShown = true
  const kind = result.operation.kind === 'mutation' ? 'mutation' : 'query'
  Notify.create({ type: 'warning', message: rateLimitMessage(result.error, kind) })
}

/**
 * Holds mutations off the network while the cooldown runs.
 * Subscriptions pass: they ride the live socket, and EDFS subscriptions do no
 * subgraph fetch the limiter counts. Belongs AFTER the cacheExchange (a
 * cached answer costs the router nothing) and before tracing (a held-back
 * operation has no request to time).
 */
export function rateLimitExchange(): Exchange {
  return ({ client, forward }) =>
    (ops$) => {
      const activeQueries = new Map<number, Operation>()
      const tagged = pipe(
        ops$,
        onPush((op) => {
          if (op.kind === 'query') activeQueries.set(op.key, op)
          else if (op.kind === 'teardown') activeQueries.delete(op.key)
        }),
        map((op) => ({
          op,
          held: op.kind === 'mutation' && cooldownRemainingMs() > 0,
        })),
        share,
      )
      const held = pipe(
        tagged,
        filter((t) => t.held),
        map((t) => heldResult(t.op)),
      )
      const sent = forward(
        pipe(
          tagged,
          filter((t) => !t.held),
          map((t) => t.op),
        ),
      )
      return pipe(
        merge([held, sent]),
        onPush((result) => {
          const refetch =
            result.operation.kind === 'mutation' &&
            isRateLimited(result.error) &&
            !notSent(result.error)
          observe(result)
          if (refetch) {
            for (const query of activeQueries.values()) {
              client.reexecuteOperation(
                makeOperation('query', query, { requestPolicy: 'network-only' }),
              )
            }
          }
        }),
      )
    }
}
